/**
 * ILF/DLF response cache + fetch status.
 *
 * Cache key is `"{zip}|{specialitiesid}|{effectiveDate}"`. Repeating the
 * same tuple returns the cached `QuoteFormData` (zero network); changing
 * any of the three invalidates the hit.
 *
 *   ilfDlfStore.setIlfDlf(key, data);
 *   const cached = ilfDlfStore.getIlfDlf(key);
 *   useStore(ilfDlfStore, (s) => s.ilfDlfResponse);
 */
class IlfDlfStore {
  #listeners = new Set();
  /** @type {{ key: string, data: import("@/shared/dtos").QuoteFormData } | null} */
  #slot = null;
  #error = null;
  #loading = false;

  cacheKey({ zipcode, specialtiesid, effectiveDate } = {}) {
    return `${zipcode || ""}|${specialtiesid || ""}|${effectiveDate || ""}`;
  }

  /** @returns {import("@/shared/dtos").QuoteFormData | null} */
  getIlfDlf(key) {
    return this.#slot?.key === key ? this.#slot.data : null;
  }

  /** @returns {import("@/shared/dtos").QuoteFormData | null} */
  get ilfDlfResponse() { return this.#slot?.data ?? null; }

  setIlfDlf(key, data) {
    this.#slot = data ? { key, data } : null;
    this.#notify();
  }

  clearResponse() {
    this.#slot = null;
    this.#notify();
  }

  get ilfDlfError() { return this.#error; }
  set ilfDlfError(err) {
    this.#error = err;
    this.#notify();
  }

  get ilfDlfLoading() { return this.#loading; }
  set ilfDlfLoading(flag) {
    this.#loading = Boolean(flag);
    this.#notify();
  }

  /** Recover the input tuple from the cached response (for rehydration). */
  get cacheParams() {
    const data = this.#slot?.data;
    if (!data) return null;
    return {
      zipcode: data.zipcode || "",
      specialtiesid: data.specialtiesid || 0,
      effectiveDate: data.effectiveDate || "",
    };
  }

  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify() { this.#listeners.forEach((l) => l()); }

  clear() {
    this.#slot = null;
    this.#error = null;
    this.#loading = false;
    this.#notify();
  }
}

const ilfDlfStore = new IlfDlfStore();
export default ilfDlfStore;
